import { FormEvent, useEffect, useState } from 'react';
import { ClipboardList, RotateCcw } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { formatInventoryReceiptSummary } from '../lib/inventoryQuantityFormatting';

type AdvancedInventory = {
  suppliers: Array<{ id: string; name: string; email?: string | null; phone?: string | null }>;
  purchaseRequests: Array<{ id: string; itemId: string; requestedQuantity: number; status: string }>;
  purchaseOrders: Array<{ id: string; supplierId?: string | null; status: string; expectedAt?: string | null }>;
  goodsReceipts: Array<{ id: string; itemId: string; quantity: number; enteredQuantity?: number; enteredUnitLabel?: string | null; baseQuantityReceived?: number; baseUnitLabel?: string | null; locationId: string; receivedAt: string }>;
  lotRecalls: Array<{ id: string; lotId: string; status: string; reason: string }>;
  transfers: Array<{ id: string; itemId: string; fromLocationId: string; toLocationId: string; quantity: number; unit: string }>;
};

export default function AdvancedInventoryPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<AdvancedInventory | null>(null);
  const [expiringLots, setExpiringLots] = useState<Array<any>>([]);
  const [supplierName, setSupplierName] = useState('');
  const [error, setError] = useState('');

  async function load() {
    setError('');
    try {
      const [advanced, expiry] = await Promise.all([
        fetchJson<AdvancedInventory>('/inventory/advanced'),
        fetchJson<{ data: Array<any> }>('/inventory/expiry-dashboard'),
      ]);
      setData(advanced);
      setExpiringLots(expiry.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load advanced inventory.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createSupplier(event: FormEvent) {
    event.preventDefault();
    if (!supplierName.trim()) return;
    await fetchJson('/inventory/suppliers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: supplierName }),
    });
    setSupplierName('');
    await load();
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Advanced inventory</p>
          <h1 className="text-2xl font-semibold text-slate-900">Suppliers, purchasing, expiry, and recalls</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><RotateCcw size={16} /> Refresh</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      <form onSubmit={createSupplier} className="flex gap-2 rounded-lg border border-slate-200 bg-white p-4">
        <input value={supplierName} onChange={(event) => setSupplierName(event.target.value)} placeholder="Supplier name" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Add supplier</button>
      </form>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Suppliers" rows={(data?.suppliers || []).map((row) => [row.name, row.email || row.phone || 'Active'])} />
        <Panel title="Purchase requests" rows={(data?.purchaseRequests || []).map((row) => [row.itemId, `${row.requestedQuantity} · ${row.status}`])} />
        <Panel title="Purchase orders" rows={(data?.purchaseOrders || []).map((row) => [row.id, row.status])} />
        <Panel title="Goods received" rows={(data?.goodsReceipts || []).map((row) => {
          const receiptSummary = formatInventoryReceiptSummary(row);
          return [row.itemId, receiptSummary.secondary ? `${receiptSummary.primary} received · ${receiptSummary.secondary}` : `${receiptSummary.primary} received`];
        })} />
        <Panel title="Stock transfers" rows={(data?.transfers || []).map((row) => [row.itemId, `${row.quantity} ${row.unit} · ${row.fromLocationId} to ${row.toLocationId}`])} />
        <Panel title="Expiry dashboard" rows={expiringLots.map((row) => [row.itemName || row.itemId, row.expirationDate ? new Date(row.expirationDate).toLocaleDateString() : 'No expiry'])} />
        <Panel title="Lot recalls" rows={(data?.lotRecalls || []).map((row) => [row.lotId, `${row.status} · ${row.reason}`])} />
      </div>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><ClipboardList size={16} /> {title}</div>
      <div className="mt-3 divide-y divide-slate-100">
        {rows.length ? rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="flex items-center justify-between py-2 text-sm">
            <span className="text-slate-700">{label}</span>
            <span className="text-slate-500">{value}</span>
          </div>
        )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
      </div>
    </div>
  );
}
